import React from 'react';
import styles from './CategoryPill.module.css';

/**
 * CategoryPill — outlined pill for category-style tags (STRIDE,
 * threat source, classification labels, operation kind, etc.).
 * Default style is a neutral wash + neutral border + --text-primary
 * label; an optional `tone` prop tints the background / border /
 * label with the matching status color so the pill itself carries
 * the semantic. Distinct from:
 *
 *   - `StatusPill`  — dot + label on a neutral wash (state tags)
 *   - `LevelBadge`  — filled background with auto-contrast text
 *                     (severity / rating swatches)
 */

type CategoryTone = 'neutral' | 'success' | 'warning' | 'error' | 'info';

export interface CategoryPillProps {
  /** Visible text. */
  label: React.ReactNode;
  /** Tints bg + border + label (default 'neutral'). */
  tone?: CategoryTone;
  /** Arbitrary CSS color (hex / token var / rgb()). When provided, overrides
   *  `tone` with the same 12%-wash / 28%-border / full-colour-label treatment
   *  via `color-mix`. */
  color?: string;
  /** 'sm' is for dense table rows. */
  size?: 'sm' | 'md';
  /** Optional wrapper class for one-off tweaks. */
  className?: string;
}

const TONE_CLASS: Record<CategoryTone, string> = {
  neutral: '',
  success: styles.toneSuccess,
  warning: styles.toneWarning,
  error:   styles.toneError,
  info:    styles.toneInfo,
};

const colorStyle = (color?: string): React.CSSProperties | undefined =>
  color
    ? {
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
        borderColor: `color-mix(in srgb, ${color} 28%, transparent)`,
        color,
      }
    : undefined;

const CategoryPill = ({
  label,
  tone = 'neutral',
  color,
  size = 'md',
  className = '',
}: CategoryPillProps) => (
  <span
    className={`${styles.pill} ${size === 'sm' ? styles.sm : ''} ${color ? '' : (TONE_CLASS[tone] || '')} ${className}`.trim()}
    style={colorStyle(color)}
  >
    {label}
  </span>
);

export default CategoryPill;
